import React, { useEffect, useState } from 'react';
import { AiOutlineArrowDown, AiOutlineArrowUp } from 'react-icons/ai';
import ReactPaginate from 'react-paginate';
import { useNavigate, useParams } from 'react-router-dom';

import axios from 'axios';

import Layout from '../../Layout';
import Loader from '../../components/CustomUI/Loader';
import NodataBox from '../../components/CustomUI/NoDataBox';
import { getServiceUrl } from '../../config';

import PatientBasicDetails from './PatientBasicDetails';
import TestList from './TestList';

const tdclass = 'text-start text-wrap text-sm py-4 px-4';

const apiBaseUrl = getServiceUrl();

export default function Diseases() {
  const patientData =
    localStorage.getItem('patientData') !== null
      ? JSON.parse(localStorage.getItem('patientData'))
      : [];

  const navigate = useNavigate();
  const { patientguid } = useParams();
  const [diseaseList, setDiseaseList] = useState([]);
  const [testList, setTestList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0); // Page index starts at 0
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [sortConfig, setSortConfig] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDisease, setSelectedDisease] = useState(null);
  const [isDetailsPopupOpen, setIsDetailsPopupOpen] = useState(false);

  const getTestDetails = async (diseaseName) => {
    const formBody = { condition_name: diseaseName };
    const config = {
      headers: { rdxtoken: localStorage.getItem('tokenId') },
    };

    try {
      const response = await axios.post(
        apiBaseUrl + 'doctors/tests',
        formBody,
        config,
      );
      setTestList(response.data);
    } catch (error) {
      console.error('Error fetching test details:', error);
    }
  };

  useEffect(() => {
    const fetchDiseasePredictions = async () => {
      try {
        const config = {
          headers: {
            rdxtoken: localStorage.getItem('tokenId'),
            patientguid: patientguid,
          },
        };
        const response = await axios.get(
          apiBaseUrl + 'doctors/predict',
          config,
        );
        setDiseaseList(response.data.predictions);
        setFilteredData(response.data.predictions);
      } catch (error) {
        console.error('Error fetching disease predictions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDiseasePredictions();
  }, [patientguid]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setIsDetailsPopupOpen(false);
      }
    };

    if (isDetailsPopupOpen) {
      window.addEventListener('keydown', handleEsc);
    }

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isDetailsPopupOpen]);

  const handleClosePopup = () => {
    setIsDetailsPopupOpen(false); // Close the popup
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }

    const sortedData = [...filteredData].sort((a, b) => {
      if (a[key] < b[key]) {
        return direction === 'asc' ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setSortConfig({ key, direction });
    setFilteredData(sortedData);
  };

  const handleRowClick = (disease) => {
    navigate(`/patients/diseasedetails/${patientguid}/${disease.Disease}`);
  };

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = diseaseList.filter((item) =>
      Object.values(item).some((value) =>
        value.toString().toLowerCase().includes(query),
      ),
    );

    setFilteredData(filtered);
    setCurrentPage(0); // Reset to first page on search
  };

  const handleOpenDetailsPopup = (disease) => {
    setSelectedDisease(disease);
    getTestDetails(disease.Disease);
    setIsDetailsPopupOpen(true);
  };

  const handleCloseDetailsPopup = () => {
    setIsDetailsPopupOpen(false);
  };

  // Pagination logic
  const indexOfLastItem = (currentPage + 1) * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (data) => {
    setCurrentPage(data.selected);
  };

  return (
    <Layout patientguid={patientguid}>
      {patientData ? <PatientBasicDetails patientData={patientData} /> : null}
      <React.Fragment>
        {loading ? (
          <Loader />
        ) : (
          <React.Fragment>
            <div className="my-8 rounded-xl border border-gray-300 bg-white p-6 shadow-md">
              <div className="mb-4 flex items-center justify-between">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="w-100% rounded-md border border-gray-400 p-2 shadow-sm transition duration-200 focus:outline-none focus:ring-1 focus:ring-blue-900"
                />
                <div className="text-sm text-gray-700">
                  Total Records:{' '}
                  <span className="mr-1 font-semibold">
                    {filteredData.length}
                  </span>
                </div>
              </div>
              {currentItems.length > 0 ? (
                <>
                  <div className="mb-4 overflow-x-auto rounded-md border border-gray-300">
                    <table className="w-full table-fixed bg-gray-100">
                      <thead className="rounded-t-lg bg-blue-900 text-white">
                        <tr>
                          <th
                            onClick={() => handleSort('Disease')}
                            className={`w-2/3 cursor-pointer border-b border-gray-300 p-3 text-left`}
                          >
                            Disease{' '}
                            {sortConfig && sortConfig.key === 'Disease' ? (
                              sortConfig.direction === 'asc' ? (
                                <AiOutlineArrowUp className="ml-1 inline-block" />
                              ) : (
                                <AiOutlineArrowDown className="ml-1 inline-block" />
                              )
                            ) : null}
                          </th>
                          <th
                            onClick={() => handleSort('RDX_Rank')}
                            className={`cursor-pointer border-b border-gray-300 p-3 text-left`}
                          >
                            RDX Rank{' '}
                            {sortConfig && sortConfig.key === 'RDX_Rank' ? (
                              sortConfig.direction === 'asc' ? (
                                <AiOutlineArrowUp className="ml-1 inline-block" />
                              ) : (
                                <AiOutlineArrowDown className="ml-1 inline-block" />
                              )
                            ) : null}
                          </th>
                          <th className="border-b border-gray-300 p-3 text-left">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((item, index) => (
                          <tr
                            key={index}
                            className="cursor-pointer border-b border-gray-200 hover:bg-gray-50"
                          >
                            <td
                              className={tdclass}
                              onClick={() => handleRowClick(item)}
                              title={item.Disease}
                            >
                              {item.Disease}
                            </td>
                            <td className={tdclass}>{item.Rank}</td>
                            <td className={tdclass}>
                              <button
                                className="rounded-md bg-blue-500 px-4 py-1 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDetailsPopup(item);
                                }}
                              >
                                Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <ReactPaginate
                    previousLabel={'« Previous'}
                    nextLabel={'Next »'}
                    breakLabel={'...'}
                    pageCount={Math.ceil(filteredData.length / itemsPerPage)}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={5}
                    onPageChange={handlePageChange}
                    containerClassName={'pagination'}
                    pageClassName={'page-item'}
                    pageLinkClassName={'page-link'}
                    previousClassName={'page-item'}
                    previousLinkClassName={'page-link'}
                    nextClassName={'page-item'}
                    nextLinkClassName={'page-link'}
                    breakClassName={'page-item'}
                    breakLinkClassName={'page-link'}
                    activeClassName={'active'}
                  />
                </>
              ) : (
                <NodataBox
                  mainText="You test details found"
                  subText="We will update the test details soon"
                />
              )}
            </div>
            {isDetailsPopupOpen && (
              <TestList
                isOpen={isDetailsPopupOpen}
                onClose={handleCloseDetailsPopup}
                testList={testList}
              />
            )}
          </React.Fragment>
        )}
      </React.Fragment>
    </Layout>
  );
}
